import { useEffect } from 'react'

import { useFormContext, type FieldErrors, type FieldPath, type FieldValues, type PathValue } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { ComboboxField } from '@/components/ui/combobox'
import { useSuspenseVendedoresUnidadeNegocio } from '@/features/proposta/brasil/hooks'
import { CardEtapaInicial } from '@/features/proposta/shared/components/card-etapa-inicial'
import { usePropostaContext } from '@/features/proposta/shared/context/proposta-context'

export const CardVendedorResponsavel = <TFieldValues extends FieldValues = FieldValues>() => {
  const { t } = useTranslation('proposta')
  const { vendedorAlocadoIdPath } = usePropostaContext()
  const {
    formState: { errors },
    watch,
    control,
    setValue,
  } = useFormContext<TFieldValues>()

  const vendedorSelecionadoId = watch('informacoesIniciais.vendedor.id' as FieldPath<TFieldValues>)
  const vendedorAlocadoId = watch(vendedorAlocadoIdPath as FieldPath<TFieldValues>)

  const erroVendedor = errors.informacoesIniciais
    ? (errors.informacoesIniciais as FieldErrors<TFieldValues>).vendedor?.message
    : undefined

  const variant = vendedorSelecionadoId && !erroVendedor ? 'completed' : 'default'
  const { data: vendedores } = useSuspenseVendedoresUnidadeNegocio()

  useEffect(() => {
    if (vendedorAlocadoId) {
      const vendedorAlocadoNaListaVendedores = vendedores.find((vendedor) => vendedor.id === vendedorAlocadoId)

      if (!vendedorSelecionadoId && vendedorAlocadoNaListaVendedores?.id) {
        const vendedorAlocado = {
          id: vendedorAlocadoNaListaVendedores.id,
          possuiDadosDeComissao: vendedorAlocadoNaListaVendedores.possuiDadosDeComissao,
          nome: vendedorAlocadoNaListaVendedores.nome,
        }
        setValue(
          'informacoesIniciais.vendedor' as FieldPath<TFieldValues>,
          vendedorAlocado as PathValue<TFieldValues, FieldPath<TFieldValues>>,
          { shouldValidate: true },
        )
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <CardEtapaInicial variant={variant}>
      <CardEtapaInicial.Content
        label={t('informacoes_iniciais.cards.vendedor.titulo')}
        description={t('informacoes_iniciais.cards.vendedor.selecione')}
      />
      <CardEtapaInicial.Action>
        <ComboboxField
          data-testid="selecao-vendedor"
          name={'informacoesIniciais.vendedor' as FieldPath<TFieldValues>}
          control={control}
          valueMode="object"
          labelProperty="nome"
          valueProperty="id"
          objectProperties={['id', 'nome', 'possuiDadosDeComissao']}
          placeholder={t('informacoes_iniciais.cards.selecione')}
          options={vendedores}
          hideClearButton={false}
          initialValue={{ id: '', nome: '', possuiDadosDeComissao: false }}
        />
      </CardEtapaInicial.Action>
    </CardEtapaInicial>
  )
}
